import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Link, useNavigate } from 'react-router-dom';
import { useSortable as useDNDKitSortable } from '@dnd-kit/sortable';

import {
    closestCenter,
    DndContext,
    DragStartEvent,
    DragOverlay,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import Chip from "@mui/joy/Chip";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Tooltip from "@mui/joy/Tooltip";
import Button from "@mui/joy/Button";
//import Grid from "@mui/joy/Grid";
//import Checkbox from "@mui/joy/Checkbox";

import Textarea from "@mui/joy/Textarea";

//import { WORK_ITEM_FONT_SIZE } from 'components/ListicleWorkItem';

import useUserIdentity from 'hooks/useUserIdentity';
import useCollabs from 'hooks/useCollabs';
import useFHRequest from 'hooks/useFHRequest';
import useWorkKey from 'hooks/useWorkKey';

import ListicleWorkItem, { ListicleWorkItemCheckbox } from 'components/ListicleWorkItem';
import SortableItem from 'components/SortableItem';

import { CollabProps } from 'stores/collab_store';
import { FHRequestType } from 'stores/fhrequest_store';

import { ProjectProps } from 'stores/project_store';
import getIcon from 'utils/getIcon';
// import { IconButton } from '@mui/material';
// import { NewIdentityForm } from './IdentityForm';
import { useSortable as useFHSortable } from 'hooks/useSortable';
//import useWorkItem from 'hooks/useWorkItem';
//import { UuidType } from 'utils/types';

import Error from 'components/Error';

export const FONT_SIZE = 'var(--joy-fontSize-xl2)';
export const PROJECT_HEADER_FONT_SIZE = FONT_SIZE;

type ListicleProjectHeaderProps = {
    project: ProjectProps;

    showNew?: boolean;
    showCount?: boolean;
}

export const ListicleProjectHeader = ({ project, showNew = true }: ListicleProjectHeaderProps) => {
    const { currentIdentity } = useUserIdentity();
    // `showNew` show the new button
    // `showCount` show the count of collabs
    // `editable` if you can edit the title in place
    // if editable is false, it's a link to the project page
    const HandleIcon = getIcon('SwapVert');
    const [over, setOver] = useState<string | null>(null);


    const {
        attributes,
        listeners,
    } = useDNDKitSortable({ id: project.uuid });

    return (
        <Stack
            spacing={1}
            direction="row"
            justifyContent="space-between"
            alignItems="center"

            onClick={() => project.uuid && setOver(project.uuid)}
            onMouseOver={() => project.uuid && setOver(project.uuid)}
            onMouseOut={() => setOver(null)}>

            <Stack direction="row">

                <Stack>
                    <Link to={`/project/${project.uuid}`}>
                    <Typography
                                                              ml={1}
                            sx={{ fontSize: FONT_SIZE }}
                            level="title-lg">
                            {project.title}</Typography>
                    </Link>
                </Stack>

                <div className="dnd-knob" {...attributes} {...listeners}>
                    <Tooltip arrow
                        key={project.uuid}
                        title="Drag to reorder"
                        placement="top">
                        <HandleIcon
                            sx={{
                                opacity: over == project.uuid ? 1 : 0,
                                rotate: over == project.uuid ? '0deg' : '-90deg',
                                width: over == project.uuid ? '42px' : '0px',
                                fontSize: '200%',
                                pt: 1,
                                color: 'divider',
                                transition: 'all 0.2s ease-in-out'
                            }} />
                    </Tooltip>
                </div>
            </Stack>
            <Stack direction="row">
                {showNew && currentIdentity.uuid == project.identity &&
                    <NewCollabButton project={project} />}
            </Stack>
        </Stack>
    );
}

export const NewCollabButton = ({ project }: { project: ProjectProps }) => {
    const navigate = useNavigate();
    const [turned, setTurned] = useState(false);

    const AddIcon = getIcon('Add');
    const url = project.uuid ? `/project/${project.uuid}/collab/new` : '/collab/new';
    const copy = project.title ?
        'Create a new collab in ' + project.title :
        'Create a new collab';

    return (
        <Tooltip
            arrow
            title={copy}>
            <Button
                sx={{
                    transition: "all 0.4s ease-in-out"
                }}
                className={turned ? "icon-tilt-back" : "icon-tilt"}
                onClick={() => {
                    if (turned) {
                        window.history.back();
                    }
                    else {
                        navigate(url)
                    }
                    setTurned(!turned);
                }}
                endDecorator={<AddIcon />}
                variant="plain"
            >
                <Stack display={{ xs: 'none', sm: 'block' }}>New</Stack>
            </Button>
        </Tooltip>
    );

}


const ListicleCollabNew = ({project}:{project: ProjectProps}) => {
    const [hovered, setHovered] = useState(false);
    const [editing, setEditing] = useState(false);
    const [title, setTitle] = useState('');
    const [newlySaved, setNewlySaved] = useState(false);
    const [creatingCollab, setCreatingCollab] = useState(false);
    const { currentIdentity } = useUserIdentity();
    const workKey = useWorkKey();
    const { createCollab, createCollabByDedicated } = useCollabs();

    useEffect(() => {
        if (newlySaved) {
            setTimeout(() => {
                setNewlySaved(false);
            }, 1_000);
        }
    }, [newlySaved]);

    const doCreate = async (collab: CollabProps) => {
        if (currentIdentity.role == 'assistant') {
            if(!workKey) {
                Error('No identity loaded');
                return;
            };
            return createCollabByDedicated(workKey, collab);
        }
        return createCollab(collab);
    };

    const doSave = () => {
        console.log('doSave', title);

        setEditing(false);
        setCreatingCollab(true);

        const collab = {
            description: title.trimEnd(),
            variant: 'collab',
            project: project.uuid,
            identity: project.identity,
        } as CollabProps;

        doCreate(collab)
            .then(() => {
                setCreatingCollab(false);
                setTitle('');
                setNewlySaved(true);
                setEditing(false);
            })
        .catch((e) => {
            console.error('doSave', e);
            Error("There was an error");
            setEditing(true);
        });
    }

    return (
        <Stack spacing={1}
            onMouseOver={() => setHovered(true)}
            onMouseOut={() => setHovered(false)}
            alignItems="flex-start">
            {/*
            <Grid
                pt={1}
                xs={1} sx={{ maxWidth: 32 }}
            >
                <Stack
                    sx={{
                        width: 32,
                        height: 32,
                        alignItems: 'center',
                        justifyContent: 'center',
                        justifyItems: 'center',
                    }}>
                    <Checkbox
                        sx={{
                            opacity: hovered ? 1 : 0.3,
                            transition: 'all 0.3s ease-in-out'
                        }}
                        checked={false}
                        onChange={() => {}}
                    />
                </Stack>

            </Grid>
            */}

            <Stack pl={1} py={0.5} spacing={1} width="100%">

                {!creatingCollab && editing ?
                    <Stack direction="row" spacing={1} width="100%" >
                        <Textarea autoFocus
                            onChange={(e) => setTitle(e.target.value)}
                            value={title}
                            onBlur={() =>
                                setTimeout(() => {
                                    !title && setEditing(false)
                                }, 100)
                            }
                            onKeyUp={(e) => {
                                if (e.key == "Escape") { setEditing(false) }
                                if (e.key == "Enter" && !e.shiftKey) { doSave() }
                            }}
                                                                         sx={{ width: "100%" }}
                            placeholder="What are we doing?" />
                            {title.length > 0 &&
                            <Button
                                onClick={doSave}
                                disabled={title.length == 0}
                                size="sm"
                                variant="plain"
                                sx={{
                                    //                                    opacity: hovered ? 1 : 0.3
                                }}
                            >Save</Button>}
                    </Stack>
                    :
                    <Stack direction="row">
                    <Button
                        size="sm"
                        onClick={() => setEditing(!editing)}
                        sx={{
                            opacity: hovered ? 0.6 : 0.2,
                            transition: 'all 0.3s ease-in-out'
                        }}
                        variant="plain">
                        {    (newlySaved ? 'Saved!' :
                                (creatingCollab ? 'Saving...' :
                                    (hovered ? "Create a new task..." : "Create a new task..."))) }
                        </Button>
                        </Stack>
                }

            </Stack>
        </Stack>


    );
};



const ListicleProject = observer(({ project, hideTitle = false }: { project: ProjectProps, hideTitle?: boolean }) => {

    // const [loading, setLoading] = useState(true);

    const { getCollabsByProject, fetchCollabsByProject } = useCollabs();
    const { currentIdentity } = useUserIdentity();
    const { getListByProject, fetchListByProject } = useFHRequest();
    const [activeId, setActiveId] = useState('');
    const [showingCompleted, showCompleted] = useState(false);
    const collabs = getCollabsByProject(currentIdentity.uuid, project.uuid, project.key);
    const requests = getListByProject(project.key);

    const {
        items,
        onSortEnd, // , getSorted,
        updateItems, sensors
    } = useFHSortable(`items-${project.uuid}`);

    // const workItems = items as CollabProps | FHRequestType;
    // const { complete } = useWorkItem();

    useEffect(() => {
        updateItems([...collabs, ...requests]);
    }, [collabs.length, requests.length]);

    useEffect(() => {
        if (project.uuid && currentIdentity?.uuid) {
            loadCollabsAndProjects();
        }
    }, [fetchCollabsByProject,
        project.uuid,
        currentIdentity?.uuid,
        fetchListByProject]);

    function loadCollabsAndProjects() {
        fetchCollabsByProject(currentIdentity.uuid, project.uuid);
        fetchListByProject(project.key).then(() => {
            // setLoading(false);
        });
    }

    useEffect(() => {
        if (project.uuid && currentIdentity?.uuid) {
            loadCollabsAndProjects();
        }
    }, [fetchCollabsByProject,
        project.uuid,
        currentIdentity?.uuid,
        fetchListByProject]);

    function handleDragStart(event: DragStartEvent) {
        const { active } = event;
        setActiveId(active.id.toString());
    }

    /*
    function onCheck(
        item: FHRequestType | CollabProps,
        checked: boolean
    ) {
        console.log('onCheck', item, checked);
        complete(currentIdentity.uuid, item.uuid, checked);
    }
     */
    // get the activeItem based on activeId
    const activeItem = items.find((i) => i.uuid == activeId);
    const completedItems = items.filter((i) => i?.completed);

    return (
        <Stack sx={{ width: '100%' }}>

            {!hideTitle &&
                <ListicleProjectHeader
                    project={project} />
            }

            <Stack pt={1}>
                    <ListicleCollabNew
                        project={project} />

                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={onSortEnd}
                >
                    <SortableContext
                        items={items.map((p) => p.uuid)}
                        strategy={verticalListSortingStrategy}
                    >
                        {items
                            .filter((i) =>
                                showingCompleted ? true : !i.completed)
                            .map((item) =>
                                <SortableItem key={item.uuid} id={item.uuid}>
                                <ListicleWorkItem key={item.uuid} item={item as CollabProps | FHRequestType} />
                                {/*
                                    <ListicleWorkItemCheckbox
                                        key={item.uuid}
                                        onCheck={onCheck}
                                        item={item as CollabProps | FHRequestType} />
                                  */}
                                </SortableItem>
                            )}
                    </SortableContext>

                    <ListicleCollabNew
                        project={project} />

                <DragOverlay>
                    {activeItem ?
                        <ListicleWorkItemCheckbox
                            onCheck={() => {}}
                            key={activeItem.uuid}
                            item={activeItem as CollabProps | FHRequestType} />
                          :
                        null
                    }
                </DragOverlay>

            </DndContext>

                { completedItems.length > 0 &&
                    <Stack m={1} direction="row" justifyContent="center">
                        <Tooltip arrow
                            title={ showingCompleted ?
                                'Hide completed' :
                                'Show completed'
                            }>
                        <Chip
                            onClick={() => showCompleted(!showingCompleted)}
                        >{ completedItems.length } completed</Chip>
                        </Tooltip>
                    </Stack> }

            </Stack>
        </Stack>
    );
});
export default ListicleProject;
